// src/components/SonoAI.js
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { SCAN_ENUMS } from './ReportWriter';

const SonoAI = ({ userProfile }) => {
  // --- UI State ---
  const [scanType, setScanType] = useState('ABDOMINAL');
  const [impression, setImpression] = useState('');
  const [status, setStatus] = useState('');

  // --- AI State ---
  const [aiResponseTitle, setAiResponseTitle] = useState(
    'How can I help you today?'
  );
  const [aiResponseContent, setAiResponseContent] = useState('');
  const [aiReferences, setAiReferences] = useState([]);

  // --- Custom Professional Dialog State ---
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
    inputValue: '',
    onConfirm: null
  });

  const [findings, setFindings] = useState([
    {
      id: 'liver',
      organ: 'Liver',
      finding: '',
      measurement: ''
    },
    {
      id: 'gallbladder',
      organ: 'Gallbladder',
      finding: '',
      measurement: ''
    },
    {
      id: 'pancreas',
      organ: 'Pancreas',
      finding: '',
      measurement: ''
    },
    {
      id: 'spleen',
      organ: 'Spleen',
      finding: '',
      measurement: ''
    },
    {
      id: 'kidney-r',
      organ: 'Right Kidney',
      finding: '',
      measurement: ''
    },
    {
      id: 'kidney-l',
      organ: 'Left Kidney',
      finding: '',
      measurement: ''
    },
    {
      id: 'aorta',
      organ: 'Aorta/IVC',
      finding: '',
      measurement: ''
    }
  ]);

  // ---------------------------------------------------------------
  // SAFE HEADER INJECTION
  // ---------------------------------------------------------------
  useEffect(() => {
    const workspace = document.querySelector('.workspace');

    if (workspace) {
      workspace.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const topBar = document.querySelector('.top-bar');

    const customHeader = document.createElement('div');
    customHeader.id = 'sonoai-custom-header';

    customHeader.innerHTML = `
      <div style="
        display:flex;
        align-items:center;
        gap:12px;
        font-family:'Inter',sans-serif;
      ">
        <div style="
          background:#2563eb;
          border-radius:6px;
          padding:5px;
          display:flex;
          align-items:center;
          justify-content:center;
          box-shadow:0 2px 4px rgba(37,99,235,0.2);
        ">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
            <path d="M4 10h3v4H4zm6-4h3v12h-3zm6 6h3v6h-3z"/>
          </svg>
        </div>

        <span style="
          font-size:1.5rem;
          font-weight:800;
          color:#0f172a;
          letter-spacing:-0.5px;
        ">
          Sonoscribe
        </span>

        <span style="
          font-size:0.95rem;
          font-weight:500;
          color:#64748b;
          margin-left:4px;
          border-left:2px solid #e2e8f0;
          padding-left:12px;
        ">
          AI Radiology Assistant
        </span>
      </div>
    `;

    if (
      topBar &&
      !document.getElementById('sonoai-custom-header')
    ) {
      topBar.insertBefore(
        customHeader,
        topBar.firstChild
      );
    }

    return () => {
      const addedHeader =
        document.getElementById(
          'sonoai-custom-header'
        );

      if (addedHeader) {
        addedHeader.remove();
      }
    };
  }, []);

  // ---------------------------------------------------------------
  // FINDING HANDLER
  // ---------------------------------------------------------------
  const handleFindingChange = (
    index,
    field,
    value
  ) => {
    const updated = [...findings];

    updated[index] = {
      ...updated[index],
      [field]: value
    };

    setFindings(updated);
  };

  // ---------------------------------------------------------------
  // COMPILE FINDINGS
  // ---------------------------------------------------------------
  const compileFindings = () =>
    findings
      .filter(
        finding =>
          finding.finding &&
          finding.finding.trim() !== ''
      )
      .map(
        finding =>
          `${finding.organ}: ${finding.finding}${
            finding.measurement
              ? ' — ' + finding.measurement
              : ''
          }`
      )
      .join('\n');

  // ---------------------------------------------------------------
  // NORMALIZE REFERENCES
  //
  // The exact AI Search contracts do not currently return external
  // references/citations. This helper is retained for other AI
  // endpoints already used by this component.
  // ---------------------------------------------------------------
  const normalizeReferences = (
    references = []
  ) => {
    if (!Array.isArray(references)) {
      return [];
    }

    return references.map((ref, index) => {
      if (typeof ref === 'string') {
        return {
          title: ref,
          url: ref,
          source: 'Ref'
        };
      }

      return {
        title:
          ref?.title ||
          ref?.name ||
          ref?.label ||
          ref?.url ||
          `Reference ${index + 1}`,
        url:
          ref?.url ||
          ref?.link ||
          '#',
        source:
          ref?.source ||
          ref?.provider ||
          ref?.journal ||
          'Ref'
      };
    });
  };

  // ---------------------------------------------------------------
  // EXTRACT VAULT SEARCH RESULTS
  //
  // EXACT BACKEND CONTRACT:
  //
  // response
  //   data
  //     results
  //       content: [...]
  //
  // POST /api/ai/search/vault
  // ---------------------------------------------------------------
  const extractVaultSearchResults = (
    response
  ) => {
    const data = response?.data;

    if (!data || typeof data !== 'object') {
      return [];
    }

    const results = data.results;

    if (
      !results ||
      typeof results !== 'object'
    ) {
      return [];
    }

    return Array.isArray(results.content)
      ? results.content
      : [];
  };

  // ---------------------------------------------------------------
  // FORMAT VAULT SEARCH RESULTS
  //
  // EXACT RESULT MODEL:
  //
  // id
  // userId
  // name
  // description
  // gender
  // reportType
  // scanType
  // category
  // defaultFindings
  // defaultImpression
  // isDefault
  // isActive
  // phiFree
  // isFavorite
  // sourceFormat
  // tags
  // originalFilename
  // fileSize
  // usageCount
  // lastUsedAt
  // createdAt
  // updatedAt
  // ---------------------------------------------------------------
  const formatVaultSearchResults = (
    results = []
  ) => {
    if (!results.length) {
      return 'No matching reports or clinical material were found in Sono Vault.';
    }

    return results
      .map((item, index) => {
        const title =
          item?.name ||
          `Vault Result ${index + 1}`;

        const scan =
          item?.scanType || '';

        const category =
          item?.category || '';

        const description =
          item?.description || '';

        const defaultFindings =
          item?.defaultFindings || '';

        const defaultImpression =
          item?.defaultImpression || '';

        const tags = Array.isArray(
          item?.tags
        )
          ? item.tags.filter(Boolean)
          : [];

        const metadata = [
          scan
            ? `Scan Type: ${scan}`
            : '',
          category
            ? `Category: ${category}`
            : '',
          item?.reportType
            ? `Report Type: ${item.reportType}`
            : '',
          item?.gender
            ? `Gender: ${item.gender}`
            : '',
          item?.isFavorite
            ? 'Favorite'
            : '',
          item?.isDefault
            ? 'Default Template'
            : ''
        ].filter(Boolean);

        const content = [
          description
            ? `Description:\n${description}`
            : '',
          defaultFindings
            ? `Default Findings:\n${defaultFindings}`
            : '',
          defaultImpression
            ? `Default Impression:\n${defaultImpression}`
            : '',
          tags.length
            ? `Tags: ${tags.join(', ')}`
            : ''
        ].filter(Boolean);

        return [
          `${index + 1}. ${title}`,
          ...metadata,
          ...content
        ]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n\n');
  };

  // ---------------------------------------------------------------
  // FORMAT TERMINOLOGY MATCHES
  //
  // EXACT BACKEND CONTRACT:
  //
  // response
  //   data
  //     query
  //     matches: [
  //       {
  //         term,
  //         definition,
  //         category,
  //         synonyms,
  //         confusedWith,
  //         exampleUsage,
  //         relevance
  //       }
  //     ]
  //
  // POST /api/ai/search/terminology
  // ---------------------------------------------------------------
  const formatTerminologyMatches = (
    matches = []
  ) => {
    if (!Array.isArray(matches) || !matches.length) {
      return 'No terminology matches were returned for this query.';
    }

    return matches
      .map((match, index) => {
        const term =
          match?.term ||
          `Match ${index + 1}`;

        const definition =
          match?.definition || '';

        const category =
          match?.category || '';

        const synonyms = Array.isArray(
          match?.synonyms
        )
          ? match.synonyms.filter(Boolean)
          : [];

        const confusedWith =
          Array.isArray(
            match?.confusedWith
          )
            ? match.confusedWith.filter(Boolean)
            : [];

        const exampleUsage =
          match?.exampleUsage || '';

        const relevance =
          typeof match?.relevance ===
          'number'
            ? `Relevance: ${Math.round(
                match.relevance * 100
              )}%`
            : '';

        return [
          `${index + 1}. ${term}`,
          category
            ? `Category: ${category}`
            : '',
          relevance,
          definition
            ? `Definition:\n${definition}`
            : '',
          synonyms.length
            ? `Synonyms:\n• ${synonyms.join(
                '\n• '
              )}`
            : '',
          confusedWith.length
            ? `Confused With:\n• ${confusedWith.join(
                '\n• '
              )}`
            : '',
          exampleUsage
            ? `Example Usage:\n${exampleUsage}`
            : ''
        ]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n\n');
  };

  // ---------------------------------------------------------------
  // GENERATE REPORT
  // POST /vault/reports
  // ---------------------------------------------------------------
  const handleGenerateReport =
    async () => {
      setStatus(
        'Generating and saving report...'
      );

      try {
        const generatedText = findings
          .filter(
            finding =>
              finding.finding &&
              finding.finding.trim() !== ''
          )
          .map(
            finding =>
              `${finding.organ}: ${finding.finding.replace(
                /\n/g,
                ' '
              )}. ${finding.measurement.replace(
                /\n/g,
                ' '
              )}`
          )
          .join('\n\n');

        const payload = {
          patientName: 'Unknown Patient',
          patientAge: 0,
          patientSex: 'OTHER',
          patientId: '',
          scanDate:
            new Date()
              .toISOString()
              .split('T')[0],
          scanType,
          reportType: 'DIAGNOSTIC',
          clinicalHistory: '',
          findings:
            generatedText ||
            'No specific findings recorded.',
          impression:
            impression ||
            'Correlate clinically.',
          recommendation: '',
          isAiGenerated: true
        };

        await api.saveReport(payload);

        setStatus('');

        setDialog({
          isOpen: true,
          type: 'alert',
          title: 'Success',
          message:
            'Report successfully generated and saved to Sono Vault!',
          onConfirm: () =>
            setDialog(prev => ({
              ...prev,
              isOpen: false
            }))
        });
      } catch (error) {
        setStatus('');

        setDialog({
          isOpen: true,
          type: 'alert',
          title: 'Error',
          message:
            'Failed to save report: ' +
            (error?.message ||
              'Unknown error'),
          onConfirm: () =>
            setDialog(prev => ({
              ...prev,
              isOpen: false
            }))
        });
      }
    };

  // ---------------------------------------------------------------
  // SUGGEST IMPRESSION
  // POST /generate-report/suggest-impression
  //
  // This endpoint was not part of the exact AI Search contract
  // supplied, so the existing api.js integration is preserved.
  // ---------------------------------------------------------------
  const handleSuggestImpression =
    async () => {
      setStatus('Analyzing findings...');

      try {
        const compiledFindings =
          compileFindings();

        if (!compiledFindings) {
          setStatus('');

          setDialog({
            isOpen: true,
            type: 'alert',
            title: 'No Findings',
            message:
              'Please enter at least one finding before requesting an impression.',
            onConfirm: () =>
              setDialog(prev => ({
                ...prev,
                isOpen: false
              }))
          });

          return;
        }

        const res =
          await api.suggestImpression({
            findings: compiledFindings,
            scanType
          });

        const imp =
          res?.data?.impression ||
          res?.impression ||
          'Unable to generate impression.';

        setImpression(imp);

        setAiResponseTitle(
          'AI Impression Suggestion'
        );

        setAiResponseContent(imp);

        const references =
          res?.data?.verifiedReferences ||
          res?.verifiedReferences ||
          res?.data?.references ||
          res?.references ||
          [];

        setAiReferences(
          normalizeReferences(references)
        );
      } catch (error) {
        setDialog({
          isOpen: true,
          type: 'alert',
          title: 'AI Error',
          message:
            error?.message ||
            'Unable to generate an impression.',
          onConfirm: () =>
            setDialog(prev => ({
              ...prev,
              isOpen: false
            }))
        });
      } finally {
        setStatus('');
      }
    };

  // ---------------------------------------------------------------
  // SEARCH / ASK
  //
  // EXACT BACKEND CONTRACT:
  //
  // POST /api/ai/search/vault
  //
  // Query parameters:
  //   query     required
  //   scanType  optional
  //   page      Spring pageable page parameter
  //   size      Spring pageable size parameter
  //
  // Response:
  // {
  //   success: true,
  //   message: "...",
  //   data: {
  //     query: "...",
  //     interpretedAs: [...],
  //     results: {
  //       totalElements,
  //       totalPages,
  //       pageable,
  //       first,
  //       last,
  //       size,
  //       content: [...]
  //     },
  //     aiCreditsUsed,
  //     servedFromCache
  //   },
  //   timestamp: "..."
  // }
  // ---------------------------------------------------------------
  const handleSearchAsk = () => {
    setDialog({
      isOpen: true,
      type: 'prompt',
      title: 'Search Sono Vault',
      message:
        'Describe a clinical finding, report pattern, or question to search Sono Vault:',
      inputValue: '',
      onConfirm: async query => {
        setDialog(prev => ({
          ...prev,
          isOpen: false
        }));

        const cleanQuery =
          String(query || '').trim();

        if (!cleanQuery) {
          return;
        }

        setStatus('Searching Sono Vault...');

        try {
          const res =
            await api.searchVaultSemantic(
              cleanQuery,
              {
                scanType,
                page: 0,
                size: 20
              }
            );

          const data = res?.data || {};

          const results =
            extractVaultSearchResults(res);

          const interpretedAs =
            Array.isArray(
              data?.interpretedAs
            )
              ? data.interpretedAs
              : [];

          const pagination =
            data?.results || {};

          setAiResponseTitle(
            `Sono Vault Search: "${cleanQuery}"`
          );

          const resultText =
            formatVaultSearchResults(
              results
            );

          const interpretationText =
            interpretedAs.length
              ? `Interpreted As:\n• ${interpretedAs.join(
                  '\n• '
                )}`
              : '';

          const paginationText =
            typeof pagination.totalElements ===
            'number'
              ? `\n\nShowing ${results.length} result${
                  results.length === 1
                    ? ''
                    : 's'
                } of ${
                  pagination.totalElements
                } total.`
              : '';

          setAiResponseContent(
            [
              interpretationText,
              resultText,
              paginationText
            ]
              .filter(Boolean)
              .join('\n\n')
          );

          // -------------------------------------------------------
          // IMPORTANT:
          //
          // The exact /ai/search/vault response supplied by the
          // backend does NOT contain verifiedReferences,
          // references, citations, links, or URLs.
          //
          // Therefore we intentionally clear references here
          // instead of inventing them from unsupported fields.
          // -------------------------------------------------------
          setAiReferences([]);
        } catch (error) {
          setDialog({
            isOpen: true,
            type: 'alert',
            title: 'Search Error',
            message:
              error?.message ||
              'Unable to search Sono Vault.',
            onConfirm: () =>
              setDialog(prev => ({
                ...prev,
                isOpen: false
              }))
          });
        } finally {
          setStatus('');
        }
      }
    });
  };

  // ---------------------------------------------------------------
  // TERMINOLOGY LOOKUP
  //
  // EXACT BACKEND CONTRACT:
  //
  // POST /api/ai/search/terminology
  //
  // JSON body:
  // {
  //   query: "string",
  //   scanType: "ABDOMINAL",
  //   limit: 10
  // }
  //
  // Response:
  // {
  //   success: true,
  //   message: "string",
  //   data: {
  //     query: "string",
  //     matches: [
  //       {
  //         term: "string",
  //         definition: "string",
  //         category: "string",
  //         synonyms: ["string"],
  //         confusedWith: ["string"],
  //         exampleUsage: "string",
  //         relevance: 0.1
  //       }
  //     ],
  //     provider: "string",
  //     model: "string",
  //     fallbackUsed: true,
  //     aiCreditsUsed: 0,
  //     servedFromCache: true,
  //     disclaimer: "string"
  //   },
  //   timestamp: "..."
  // }
  // ---------------------------------------------------------------
  const handleTerminologyLookup =
    () => {
      setDialog({
        isOpen: true,
        type: 'prompt',
        title: 'Terminology Lookup',
        message:
          'Enter a medical or sonographic term to look up its definition, synonyms, related terms and usage:',
        inputValue: '',
        onConfirm: async query => {
          setDialog(prev => ({
            ...prev,
            isOpen: false
          }));

          const cleanQuery =
            String(query || '').trim();

          if (!cleanQuery) {
            return;
          }

          setStatus(
            'Looking up terminology...'
          );

          try {
            // EXACT REQUEST BODY EXPECTED BY:
            // POST /api/ai/search/terminology
            const requestBody = {
              query: cleanQuery,
              scanType,
              limit: 10
            };

            const res =
              await api.searchTerminology(
                requestBody
              );

            const data = res?.data || {};

            // EXACT RESPONSE FIELD:
            // data.matches
            const matches = Array.isArray(
              data?.matches
            )
              ? data.matches
              : [];

            setAiResponseTitle(
              `Terminology: "${cleanQuery}"`
            );

            const formattedMatches =
              formatTerminologyMatches(
                matches
              );

            const metadata = [];

            if (data?.provider) {
              metadata.push(
                `Provider: ${data.provider}`
              );
            }

            if (data?.model) {
              metadata.push(
                `Model: ${data.model}`
              );
            }

            if (data?.fallbackUsed) {
              metadata.push(
                'Fallback provider used'
              );
            }

            if (data?.servedFromCache) {
              metadata.push(
                'Served from cache'
              );
            }

            const disclaimer =
              data?.disclaimer || '';

            setAiResponseContent(
              [
                formattedMatches,
                metadata.length
                  ? metadata.join(' • ')
                  : '',
                disclaimer
                  ? `Disclaimer:\n${disclaimer}`
                  : ''
              ]
                .filter(Boolean)
                .join('\n\n')
            );

            // The exact terminology contract does not contain
            // references/citations/URLs.
            setAiReferences([]);
          } catch (error) {
            setDialog({
              isOpen: true,
              type: 'alert',
              title: 'Terminology Error',
              message:
                error?.message ||
                'Unable to look up terminology.',
              onConfirm: () =>
                setDialog(prev => ({
                  ...prev,
                  isOpen: false
                }))
            });
          } finally {
            setStatus('');
          }
        }
      });
    };

  // ---------------------------------------------------------------
  // FOLLOW-UP
  // POST /vault/reports/helpers
  //
  // This endpoint was not included in the exact AI Search
  // contract supplied, so the existing integration is preserved.
  // ---------------------------------------------------------------
  const handleFollowUpQuestion =
    () => {
      setDialog({
        isOpen: true,
        type: 'prompt',
        title: 'Follow-up Question',
        message:
          'Ask a follow-up question regarding the AI response:',
        inputValue: '',
        onConfirm: async query => {
          setDialog(prev => ({
            ...prev,
            isOpen: false
          }));

          const cleanQuery =
            String(query || '').trim();

          if (!cleanQuery) {
            return;
          }

          setStatus(
            'Fetching clinical insights...'
          );

          try {
            const combined = `${aiResponseContent}\n\nFollow-up: ${cleanQuery}`;

            const res =
              await api.getReportHelpers({
                scanType,
                findings: combined
              });

            const data = res?.data || {};
            const lines = [];

            if (data.impressionSuggestion) {
              lines.push(
                `Impression Suggestion:\n${data.impressionSuggestion}`
              );
            }

            if (
              Array.isArray(
                data.clinicalDifferentials
              ) &&
              data.clinicalDifferentials.length
            ) {
              lines.push(
                `Clinical Differentials:\n• ${data.clinicalDifferentials.join(
                  '\n• '
                )}`
              );
            }

            if (
              Array.isArray(
                data.wordingSuggestions
              ) &&
              data.wordingSuggestions.length
            ) {
              lines.push(
                `Wording Suggestions:\n• ${data.wordingSuggestions.join(
                  '\n• '
                )}`
              );
            }

            setAiResponseTitle(
              `Follow-up: "${cleanQuery}"`
            );

            setAiResponseContent(
              lines.join('\n\n') ||
                'No results found.'
            );

            const rawRefs =
              data.verifiedReferences || [];

            setAiReferences(
              normalizeReferences(rawRefs)
            );
          } catch (error) {
            setDialog({
              isOpen: true,
              type: 'alert',
              title: 'AI Error',
              message:
                error?.message ||
                'Unable to fetch clinical insights.',
              onConfirm: () =>
                setDialog(prev => ({
                  ...prev,
                  isOpen: false
                }))
            });
          } finally {
            setStatus('');
          }
        }
      });
    };

  // ---------------------------------------------------------------
  // ACTION CARDS
  // ---------------------------------------------------------------
  const handleActionCardClick =
    title => {
      switch (title) {
        case 'Clinical Differential':
          setDialog({
            isOpen: true,
            type: 'prompt',
            title: 'Clinical Differential',
            message:
              "Enter a sonographic finding to get differentials (e.g., 'solid renal mass'):",
            inputValue: '',
            onConfirm: async finding => {
              setDialog(prev => ({
                ...prev,
                isOpen: false
              }));

              const cleanFinding =
                String(finding || '').trim();

              if (!cleanFinding) {
                return;
              }

              setStatus(
                'Analyzing differentials...'
              );

              try {
                const res =
                  await api.getReportHelpers({
                    scanType,
                    findings: cleanFinding
                  });

                const data =
                  res?.data || {};

                const differentials =
                  Array.isArray(
                    data.clinicalDifferentials
                  )
                    ? data.clinicalDifferentials
                    : [];

                setAiResponseTitle(
                  `Differentials for: "${cleanFinding}"`
                );

                setAiResponseContent(
                  differentials.length
                    ? `• ${differentials.join(
                        '\n• '
                      )}`
                    : 'No differentials found.'
                );

                const rawRefs =
                  data.verifiedReferences ||
                  [];

                setAiReferences(
                  normalizeReferences(
                    rawRefs
                  )
                );
              } catch (error) {
                setDialog({
                  isOpen: true,
                  type: 'alert',
                  title: 'AI Error',
                  message:
                    error?.message ||
                    'Unable to retrieve clinical differentials.',
                  onConfirm: () =>
                    setDialog(prev => ({
                      ...prev,
                      isOpen: false
                    }))
                });
              } finally {
                setStatus('');
              }
            }
          });
          break;

        case 'Impression Generator':
          handleSuggestImpression();
          break;

        case 'Wording Assistant':
          setDialog({
            isOpen: true,
            type: 'prompt',
            title: 'Wording Assistant',
            message:
              'Enter a phrase to improve:',
            inputValue: '',
            onConfirm: async phrase => {
              setDialog(prev => ({
                ...prev,
                isOpen: false
              }));

              const cleanPhrase =
                String(phrase || '').trim();

              if (!cleanPhrase) {
                return;
              }

              setStatus(
                'Improving phrasing...'
              );

              try {
                const res =
                  await api.improveWording({
                    text: cleanPhrase
                  });

                const corrected =
                  res?.data?.correctedText ||
                  res?.correctedText ||
                  'No suggestion available.';

                setAiResponseTitle(
                  'Suggested Phrasing'
                );

                setAiResponseContent(
                  corrected
                );

                const references =
                  res?.data
                    ?.verifiedReferences ||
                  res?.verifiedReferences ||
                  [];

                setAiReferences(
                  normalizeReferences(
                    references
                  )
                );
              } catch (error) {
                setDialog({
                  isOpen: true,
                  type: 'alert',
                  title: 'AI Error',
                  message:
                    error?.message ||
                    'Unable to improve wording.',
                  onConfirm: () =>
                    setDialog(prev => ({
                      ...prev,
                      isOpen: false
                    }))
                });
              } finally {
                setStatus('');
              }
            }
          });
          break;

        case 'Terminology Lookup':
          handleTerminologyLookup();
          break;

        case 'Guidelines & Protocols':
          window.open(
            'https://www.acr.org/Clinical-Resources/ACR-Appropriateness-Criteria',
            '_blank',
            'noopener,noreferrer'
          );
          break;

        case 'Verified References':
          window.open(
            'https://pubmed.ncbi.nlm.nih.gov/',
            '_blank',
            'noopener,noreferrer'
          );
          break;

        default:
          break;
      }
    };

  return (
    <>
      <style>{`
        .top-bar h2 {
          display: none !important;
        }

        .sonoai-container {
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #0f172a;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 20px;
        }

        .top-action-row {
          display: flex;
          gap: 20px;
          margin-bottom: 5px;
        }

        .top-btn {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          border-radius: 12px;
          color: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 4px 15px rgba(0,0,0,0.06);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .top-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.1);
        }

        .top-btn.search {
          background: linear-gradient(135deg, #1e40af, #3b82f6);
        }

        .top-btn.draft {
          background: linear-gradient(135deg, #ea580c, #f97316);
        }

        .top-btn-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 6px;
        }

        .top-btn-sub {
          font-size: 0.9rem;
          opacity: 0.9;
        }

        .main-grid {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .col-left {
          flex: 0.75;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .chat-container {
          background: white;
          border-radius: 16px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
          border: 1px solid #f1f5f9;
        }

        .chat-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 10px 0;
        }

        .chat-sub {
          font-size: 0.9rem;
          color: #475569;
          line-height: 1.5;
          margin: 0 0 25px 0;
        }

        .ai-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.02);
          margin-bottom: 15px;
        }

        .ai-card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
        }

        .ai-card-header h4 {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
        }

        .follow-up-btn {
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 15px;
        }

        .follow-up-btn:hover {
          background: #dbeafe;
        }

        .references-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 15px 20px;
          background: white;
        }

        .ref-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .ref-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #f1f5f9;
          transition: background 0.2s;
          cursor: pointer;
        }

        .ref-item:hover {
          background: #f8fafc;
        }

        .ref-item:last-child {
          border-bottom: none;
        }

        .ref-item-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .ref-icon {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.85rem;
          color: white;
          background: #3b82f6;
        }

        .ref-title {
          margin: 0;
          font-size: 0.85rem;
          font-weight: 700;
          color: #0f172a;
          overflow-wrap: anywhere;
        }

        .ref-sub {
          margin: 0;
          font-size: 0.75rem;
          color: #64748b;
          overflow-wrap: anywhere;
        }

        .col-right {
          flex: 1.25;
          background: white;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
          border: 1px solid #f1f5f9;
        }

        .draft-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .draft-header h2 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 700;
          color: #0f172a;
        }

        .draft-actions {
          display: flex;
          gap: 12px;
          color: #3b82f6;
        }

        .step-section {
          margin-bottom: 25px;
        }

        .step-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 5px;
        }

        .step-num {
          background: #2563eb;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          font-weight: 700;
        }

        .step-sub {
          margin: 0 0 15px 34px;
          font-size: 0.85rem;
          color: #64748b;
        }

        .scan-select {
          width: calc(100% - 34px);
          margin-left: 34px;
          padding: 12px 15px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          outline: none;
          box-sizing: border-box;
        }

        .scan-select:focus {
          border-color: #3b82f6;
        }

        .findings-table {
          width: calc(100% - 34px);
          margin-left: 34px;
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
        }

        .findings-table th {
          background: #f8fafc;
          padding: 14px 15px;
          text-align: left;
          font-size: 0.85rem;
          font-weight: 700;
          color: #334155;
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
        }

        .findings-table th:last-child {
          border-right: none;
        }

        .findings-table td {
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          padding: 0;
          vertical-align: top;
        }

        .findings-table td:last-child {
          border-right: none;
        }

        .findings-table tr:last-child td {
          border-bottom: none;
        }

        .td-organ {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 15px;
          font-weight: 600;
          font-size: 0.9rem;
          color: #0f172a;
        }

        .td-input-area {
          width: 100%;
          height: 100%;
          min-height: 50px;
          border: none;
          padding: 14px 15px;
          font-size: 0.85rem;
          color: #334155;
          resize: none;
          outline: none;
          font-family: inherit;
          box-sizing: border-box;
          background: transparent;
          line-height: 1.4;
          transition: background 0.2s;
        }

        .td-input-area:focus {
          background: #f8fafc;
        }

        .td-input-area::placeholder {
          color: #cbd5e1;
        }

        .impression-area {
          width: calc(100% - 34px);
          margin-left: 34px;
          padding: 15px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.9rem;
          outline: none;
          resize: vertical;
          min-height: 80px;
          box-sizing: border-box;
          font-family: inherit;
          line-height: 1.5;
          color: #334155;
          transition: border-color 0.2s;
        }

        .impression-area:focus {
          border-color: #3b82f6;
        }

        .action-row {
          display: flex;
          gap: 15px;
          margin-left: 34px;
          margin-top: 25px;
        }

        .btn-suggest {
          flex: 1;
          background: white;
          border: 1px solid #cbd5e1;
          color: #0f172a;
          padding: 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.2s;
        }

        .btn-suggest:hover {
          background: #f8fafc;
        }

        .btn-generate {
          flex: 1;
          background: linear-gradient(135deg, #ea580c, #f97316);
          border: none;
          color: white;
          padding: 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 6px rgba(234,88,12,0.2);
          transition: opacity 0.2s;
        }

        .btn-generate:hover {
          opacity: 0.9;
        }

        .bottom-cards-container {
          display: flex;
          gap: 15px;
          margin-top: 10px;
          flex-wrap: wrap;
        }

        .bottom-card {
          flex: 1 1 170px;
          background: white;
          border-radius: 12px;
          padding: 20px 15px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          border: 1px solid #f1f5f9;
          transition: border-color 0.2s, transform 0.2s;
        }

        .bottom-card:hover {
          border-color: #bfdbfe;
          transform: translateY(-2px);
        }

        .bc-icon {
          width: 45px;
          height: 45px;
          background: #eff6ff;
          color: #2563eb;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bc-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .bc-sub {
          font-size: 0.75rem;
          color: #64748b;
          margin: 0;
          line-height: 1.3;
        }

        .sono-footer {
          margin-top: 10px;
          text-align: center;
          font-size: 0.85rem;
          color: #475569;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding-bottom: 20px;
        }

        .check-circle {
          width: 16px;
          height: 16px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
        }

        @media (max-width: 1100px) {
          .main-grid {
            flex-direction: column;
          }

          .col-left,
          .col-right {
            width: 100%;
            flex: 1;
            box-sizing: border-box;
          }
        }

        @media (max-width: 700px) {
          .top-action-row {
            flex-direction: column;
          }

          .col-right {
            padding: 20px 15px;
          }

          .chat-container {
            padding: 20px 15px;
          }

          .draft-header {
            align-items: flex-start;
            gap: 15px;
          }

          .draft-header h2 {
            font-size: 1.2rem;
          }

          .findings-table,
          .scan-select,
          .impression-area {
            width: 100%;
            margin-left: 0;
          }

          .step-sub {
            margin-left: 0;
          }

          .action-row {
            margin-left: 0;
            flex-direction: column;
          }

          .findings-table {
            display: block;
            overflow-x: auto;
          }

          .bottom-card {
            flex: 1 1 calc(50% - 15px);
          }
        }

        @media (max-width: 480px) {
          .bottom-card {
            flex: 1 1 100%;
          }

          #sonoai-custom-header span:last-child {
            display: none;
          }
        }
      `}</style>

      {status && (
        <div className="fullscreen-loader-overlay">
          <div className="dot-loader">
            <div></div>
            <div></div>
            <div></div>
          </div>

          <div className="loading-text">
            {status}
          </div>
        </div>
      )}

      {dialog.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background:
              'rgba(15,23,42,0.6)',
            backdropFilter: 'blur(2px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            className="fade-in"
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '450px',
              maxWidth: '90%',
              boxShadow:
                '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
          >
            <h3
              style={{
                margin: '0 0 12px 0',
                fontSize: '1.25rem',
                color: '#0f172a',
                fontWeight: '700'
              }}
            >
              {dialog.title}
            </h3>

            <p
              style={{
                margin: '0 0 20px 0',
                color: '#475569',
                fontSize: '0.95rem',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {dialog.message}
            </p>

            {dialog.type === 'prompt' && (
              <input
                type="text"
                autoFocus
                value={dialog.inputValue}
                onChange={e =>
                  setDialog({
                    ...dialog,
                    inputValue:
                      e.target.value
                  })
                }
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    dialog.onConfirm(
                      dialog.inputValue
                    );
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border:
                    '1px solid #cbd5e1',
                  borderRadius: '8px',
                  marginBottom: '25px',
                  fontSize: '0.95rem',
                  outlineColor: '#3b82f6',
                  boxSizing: 'border-box'
                }}
              />
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}
            >
              <button
                onClick={() =>
                  setDialog({
                    ...dialog,
                    isOpen: false
                  })
                }
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border:
                    '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition:
                    'background 0.2s'
                }}
              >
                Cancel
              </button>

              <button
                onClick={() =>
                  dialog.onConfirm(
                    dialog.type === 'prompt'
                      ? dialog.inputValue
                      : true
                  )
                }
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#3b82f6',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition:
                    'background 0.2s'
                }}
              >
                {dialog.type === 'prompt'
                  ? 'Submit'
                  : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sonoai-container fade-in">
        <div className="sonoai-content">

          {/* =====================================================
              TOP ACTIONS
          ====================================================== */}
          <div className="top-action-row">

            <button
              className="top-btn search"
              onClick={handleSearchAsk}
            >
              <div className="top-btn-title">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>

                Search / Ask
              </div>

              <div className="top-btn-sub">
                Search Sono Vault using clinical language
              </div>
            </button>

            <button
              className="top-btn draft"
              onClick={() => {
                const scanSelect =
                  document.querySelector(
                    '.scan-select'
                  );

                if (scanSelect) {
                  scanSelect.focus();
                }
              }}
            >
              <div className="top-btn-title">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>

                Draft Report
              </div>

              <div className="top-btn-sub">
                Abdomen ultrasound
              </div>
            </button>
          </div>

          {/* =====================================================
              MAIN GRID
          ====================================================== */}
          <div className="main-grid">

            {/* ===================================================
                LEFT — AI RESPONSE
            ==================================================== */}
            <div className="col-left">

              <div className="chat-container">

                <h2 className="chat-title">
                  {aiResponseTitle}
                </h2>

                <p className="chat-sub">
                  Ask questions, search Sono Vault, look up
                  terminology, get clinical insights,
                  differentials, details, or wording help.
                  <br />
                  AI Search results are displayed directly from
                  the backend response contract.
                </p>

                <div className="ai-card">

                  <div className="ai-card-header">

                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        background: '#eff6ff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="#2563eb"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                      </svg>
                    </div>

                    <h4>
                      AI Response
                    </h4>
                  </div>

                  <div
                    style={{
                      fontSize: '0.95rem',
                      color: '#334155',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {aiResponseContent ||
                      'Submit a query using the buttons below to receive AI guidance.'}
                  </div>

                  {aiResponseContent && (
                    <button
                      className="follow-up-btn"
                      onClick={
                        handleFollowUpQuestion
                      }
                    >
                      Ask follow-up...
                    </button>
                  )}

                </div>

                {/* =================================================
                    REFERENCES
                ================================================== */}
                {aiReferences.length > 0 && (
                  <div className="references-card">

                    <div className="ref-header">
                      <span>
                        References (
                        {aiReferences.length})
                      </span>

                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {aiReferences.map(
                      (ref, idx) => (
                        <div
                          key={`${ref.url || ref.title}-${idx}`}
                          className="ref-item"
                          onClick={() => {
                            if (
                              ref.url &&
                              ref.url !== '#'
                            ) {
                              window.open(
                                ref.url,
                                '_blank',
                                'noopener,noreferrer'
                              );
                            }
                          }}
                          style={{
                            cursor:
                              ref.url &&
                              ref.url !== '#'
                                ? 'pointer'
                                : 'default'
                          }}
                        >

                          <div className="ref-item-left">

                            <div className="ref-icon">
                              {ref.source?.[0] ||
                                'R'}
                            </div>

                            <div>
                              <p className="ref-title">
                                {ref.title}
                              </p>

                              <p className="ref-sub">
                                {ref.url ||
                                  ref.source}
                              </p>
                            </div>

                          </div>

                          {ref.url &&
                            ref.url !== '#' && (
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            )}

                        </div>
                      )
                    )}

                  </div>
                )}

              </div>
            </div>

            {/* ===================================================
                RIGHT — REPORT DRAFT
            ==================================================== */}
            <div className="col-right">

              <div className="draft-header">

                <h2>
                  Draft Report Configuration
                </h2>

                <div className="draft-actions">

                  <svg
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    style={{
                      cursor: 'pointer'
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>

                  <svg
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    style={{
                      cursor: 'pointer'
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>

                </div>
              </div>

              {/* =================================================
                  STEP 1
              ================================================== */}
              <div className="step-section">

                <div className="step-title">
                  <span className="step-num">
                    1
                  </span>

                  Select Scan Type
                </div>

                <select
                  className="scan-select"
                  value={scanType}
                  onChange={e =>
                    setScanType(
                      e.target.value
                    )
                  }
                >
                  {SCAN_ENUMS.map(
                    scan => (
                      <option
                        key={scan.value}
                        value={scan.value}
                      >
                        {scan.label}
                      </option>
                    )
                  )}
                </select>

              </div>

              {/* =================================================
                  STEP 2
              ================================================== */}
              <div className="step-section">

                <div className="step-title">
                  <span className="step-num">
                    2
                  </span>

                  Organs & Findings
                </div>

                <p className="step-sub">
                  Fill findings and optional measurements
                </p>

                <table className="findings-table">

                  <thead>
                    <tr>

                      <th
                        style={{
                          width: '25%'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems:
                              'center',
                            gap: '8px'
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                            />

                            <path d="M12 8v8m-4-4h8" />
                          </svg>

                          Organ
                        </div>
                      </th>

                      <th
                        style={{
                          width: '45%'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems:
                              'center',
                            gap: '8px'
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>

                          Findings
                        </div>
                      </th>

                      <th
                        style={{
                          width: '30%'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems:
                              'center',
                            gap: '8px'
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a3 3 0 006 0M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                          </svg>

                          Measurements
                        </div>
                      </th>

                    </tr>
                  </thead>

                  <tbody>

                    {findings.map(
                      (finding, index) => (
                        <tr
                          key={
                            finding.id
                          }
                        >

                          <td>
                            <div className="td-organ">
                              {finding.organ}
                            </div>
                          </td>

                          <td>
                            <textarea
                              className="td-input-area"
                              value={
                                finding.finding
                              }
                              onChange={e =>
                                handleFindingChange(
                                  index,
                                  'finding',
                                  e.target.value
                                )
                              }
                              placeholder="Enter findings..."
                              spellCheck={
                                false
                              }
                              data-gramm="false"
                              data-gramm_editor="false"
                              data-enable-grammarly="false"
                            />
                          </td>

                          <td>
                            <textarea
                              className="td-input-area"
                              value={
                                finding.measurement
                              }
                              onChange={e =>
                                handleFindingChange(
                                  index,
                                  'measurement',
                                  e.target.value
                                )
                              }
                              placeholder="Enter measurements..."
                              spellCheck={
                                false
                              }
                              data-gramm="false"
                              data-gramm_editor="false"
                              data-enable-grammarly="false"
                            />
                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </table>

              </div>

              {/* =================================================
                  STEP 3
              ================================================== */}
              <div
                className="step-section"
                style={{
                  marginBottom: 0
                }}
              >

                <div className="step-title">
                  <span className="step-num">
                    3
                  </span>

                  Impression (summary)
                </div>

                <textarea
                  className="impression-area"
                  value={impression}
                  onChange={e =>
                    setImpression(
                      e.target.value
                    )
                  }
                  placeholder="Summarize key impressions or leave blank for AI suggestion..."
                  spellCheck={false}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />

              </div>

              {/* =================================================
                  REPORT ACTIONS
              ================================================== */}
              <div className="action-row">

                <button
                  className="btn-suggest"
                  onClick={
                    handleSuggestImpression
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>

                  Suggest Impression
                </button>

                <button
                  className="btn-generate"
                  onClick={
                    handleGenerateReport
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>

                  Generate Report
                </button>

              </div>

            </div>
          </div>

          {/* =====================================================
              ACTION CARDS
          ====================================================== */}
          <div className="bottom-cards-container">

            {/* Clinical Differential */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Clinical Differential'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Clinical Differential
              </p>

              <p className="bc-sub">
                Get condition differentials fast
              </p>
            </div>

            {/* Impression Generator */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Impression Generator'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Impression Generator
              </p>

              <p className="bc-sub">
                Generate impressions from your findings
              </p>
            </div>

            {/* Wording Assistant */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Wording Assistant'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Wording Assistant
              </p>

              <p className="bc-sub">
                Improve clarity & standard phrasing
              </p>
            </div>

            {/* Terminology Lookup */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Terminology Lookup'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M4 5h16M4 10h16M4 15h10M4 20h7" />

                  <circle
                    cx="18"
                    cy="18"
                    r="3"
                  />

                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.2 20.2L22 22"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Terminology Lookup
              </p>

              <p className="bc-sub">
                Check canonical terms, definitions & synonyms
              </p>
            </div>

            {/* Guidelines */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Guidelines & Protocols'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Guidelines & Protocols
              </p>

              <p className="bc-sub">
                ACR, EFSUMB, etc.
              </p>
            </div>

            {/* Verified References */}
            <div
              className="bottom-card"
              onClick={() =>
                handleActionCardClick(
                  'Verified References'
                )
              }
            >
              <div className="bc-icon">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>

              <p className="bc-title">
                Verified References
              </p>

              <p className="bc-sub">
                Radiopaedia, PubMed, UpToDate & more
              </p>
            </div>

          </div>

          <div className="sono-footer">
            <div className="check-circle">
              ✓
            </div>

            AI Search results are returned from Sono Vault •
            Terminology results are returned from the AI terminology service
          </div>

        </div>
      </div>
    </>
  );
};

export default SonoAI;